/**
 * Copy shown to property owners for AI features.
 * Never mention vendor billing, API keys, credits, or internal infrastructure.
 */

export const AI_SCAN_UNAVAILABLE =
  "AI photo scan isn't available right now. Please try again in a few minutes, or add tasks manually.";

export const AI_SCAN_RATE_LIMIT =
  "Too many scan requests right now. Please wait a moment and try again.";

export const AI_SCAN_EMPTY =
  "No checklist items were found in these photos. Try clearer room photos, turn on “Show low-confidence”, or add tasks manually.";

export const AI_SCAN_PHOTO_LOAD_ALL =
  "We couldn't load your room photos for scanning. Open the Photos tab, upload photos for each room, then try Generate with AI again.";

export function aiScanPhotoLoadPartial(failedCount: number): string {
  return `${failedCount} room${failedCount !== 1 ? "s" : ""} couldn't be scanned. Re-upload photos for those rooms on the Photos tab and try again.`;
}

/** Map server error_code to a safe owner-facing message (null = use other logic). */
export function userMessageForScanErrorCode(error_code?: string): string | null {
  switch (error_code) {
    case "CLAUDE_INSUFFICIENT_CREDITS":
    case "CLAUDE_ERROR":
    case "CLAUDE_AUTH":
    case "SERVER_MISCONFIGURED":
    case "OPENAI_ERROR":
    case "EMPTY_MODEL_RESPONSE":
    case "INVALID_MODEL_JSON":
      return AI_SCAN_UNAVAILABLE;
    case "CLAUDE_RATE_LIMIT":
      return AI_SCAN_RATE_LIMIT;
    case "IMAGE_FETCH_FAILED":
      return null;
    case "IMAGE_TOO_LARGE":
      return "That photo is too large to scan. Use a smaller image and try again.";
    case "INVALID_IMAGE_FORMAT":
      return "That photo format isn't supported. Save it as JPEG or PNG and try again.";
    case "OPENAI_TIMEOUT":
      return "Scan timed out for this photo. Try again or use a smaller image.";
    case "GEMINI_ERROR":
    case "GEMINI_TIMEOUT":
    case "VIDEO_FETCH_FAILED":
    case "MISSING_VIDEO_URL":
    case "MISSING_IMAGE_URL":
    case "FUNCTIONS_RESOURCE":
      return "We couldn't finish that. Nothing was saved. Try again — tap one item at a time.";
    case "VIDEO_RETIRED":
      return "We now capture one photo at a time. Open the room and tap the shutter on each thing.";
    case "GEMINI_RATE_LIMIT":
      return "Too many scan requests right now. Please wait a moment and try again.";
    case "VIDEO_TOO_LARGE":
      return "That video is too large. Film a shorter take.";
    default:
      return null;
  }
}

/** Strip vendor/infrastructure details from API messages before showing owners. */
export function sanitizeAiUserMessage(message: string, error_code?: string, status?: number): string {
  if (status === 546 || /546|compute resources|insufficient resources|worker exceeded/i.test(message)) {
    return "We couldn't finish that. Nothing was saved. Try again — tap one item at a time.";
  }
  const byCode = userMessageForScanErrorCode(error_code);
  if (byCode) return byCode;

  const lower = message.toLowerCase();
  if (
    /credit balance|anthropic|openai|gemini|supabase|api key|misconfigured|purchase credits|plans & billing|platform\.openai|console\.anthropic|billing is set up|server misconfigured/i.test(
      lower
    )
  ) {
    return AI_SCAN_UNAVAILABLE;
  }
  if (/could not load image|image url returned|image_fetch_failed|image request timed out/i.test(lower)) {
    return AI_SCAN_PHOTO_LOAD_ALL;
  }
  if (/rate limit|usage cap|too many requests/i.test(lower)) {
    return AI_SCAN_RATE_LIMIT;
  }
  if (/timed out|timeout/i.test(lower)) {
    return "Scan timed out. Try again or use a smaller photo.";
  }

  return message;
}
